import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import {
  useToast,
  Box,
  Heading,
  SimpleGrid,
  useColorModeValue,
  Skeleton,
  Text,
  Show,
  Select,
  Button,
  Flex,
  Spacer,
  Stack,
} from "@chakra-ui/react";
import { Line, Bar } from "react-chartjs-2";
import { Trans } from "@lingui/react/macro";
import { DataDump } from "../../components/DeviceComponents/DataDump";
import { DeviceMap } from "../../components/DeviceComponents/DeviceMap";
import { Link } from "react-router-dom";
import { IDevice } from "../../types/deviceInterface";

interface SeriesPoint {
  timestamp: string;
  temperature: number | null;
  humidity: number | null;
  precipitation: number | null;
  wind_speed: number | null;
  max_wind_speed: number | null;
  wind_direction: number | null;
}

const WeatherStationDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useAxios();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");

  const [series, setSeries] = useState<SeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [lastDays, setLastDays] = useState<number>(7);
  const [interval, setInterval] = useState<string>("hourly");
  const [device, setDevice] = useState<IDevice | undefined>(undefined);
  const [deviceLoading, setDeviceLoading] = useState(true);

  // remember last fetch key to prevent duplicate runs
  const lastFetchKey = useRef<string>("");

  const run = async () => {
    const key = `${id}-${lastDays}-${interval}`;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    setLoading(true);
    setFetching(true);
    try {
      const resp = await api.get(`agtech/weather-station/data/${id}/`, {
        params: { last_number_of_days: lastDays, interval },
      });
      setSeries(resp.data.series);
    } catch {
      toast({
        title: "Error fetching weather data",
        status: "error",
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };
  // fetch once when id becomes available
  useEffect(() => {
    if (id) run();
  }, [id]);

  // fetch on lastDays/interval changes
  useEffect(() => {
    run();
  }, [lastDays, interval]);

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const res = await api.get<IDevice>(`frontend/devices/current-data/${id}/`);
        setDevice(res.data);
      } catch {
        toast({
          title: "Error fetching device data",
          status: "error",
          isClosable: true,
        });
      } finally {
        setDeviceLoading(false);
      }
    };
    fetchDevice();
  }, [id]);

  const chartData = {
    labels: series.map((p) => {
      const d = new Date(p.timestamp);
      if (interval === "daily") {
        return d.toLocaleDateString();
      }
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
      });
    }),
    datasets: [
      {
        label: "Temperature",
        data: series.map((p) => p.temperature),
        borderColor: "red",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Humidity",
        data: series.map((p) => p.humidity),
        borderColor: "green",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Precipitation",
        data: series.map((p) => p.precipitation),
        borderColor: "blue",
        backgroundColor: "blue",
        fill: true,
        yAxisID: "y2",
        type: "bar",
      },
      {
        label: "Wind Speed",
        data: series.map((p) => p.wind_speed),
        borderColor: "orange",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Wind Gust",
        data: series.map((p) => p.max_wind_speed),
        borderColor: "purple",
        fill: false,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    tooltips: {
      callbacks: {
        label: function (tooltipItem: any, data: any) {
          const label = data.datasets[tooltipItem.datasetIndex].label || "";
          const value = parseFloat(tooltipItem.value);
          return `${label}: ${value.toFixed(1)}`;
        },
      },
    },
  };
  const tempData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Temperature",
        data: series.map((p) => p.temperature),
        borderColor: "red",
        fill: false,
      },
    ],
  };
  const humidityData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Humidity",
        data: series.map((p) => p.humidity),
        borderColor: "green",
        fill: false,
      },
    ],
  };
  const precipData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Precipitation",
        data: series.map((p) => p.precipitation),
        borderColor: "blue",
        backgroundColor: "blue",
        fill: true,
        type: "bar",
      },
    ],
  };
  const windSpeedData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Wind Speed",
        data: series.map((p) => p.wind_speed),
        borderColor: "orange",
        fill: false,
      },
    ],
  };
  const windGustData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Wind Gust",
        data: series.map((p) => p.max_wind_speed),
        borderColor: "purple",
        fill: false,
      },
    ],
  };
  const windDirectionData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Wind Direction",
        data: series.map((p) => p.wind_direction),
        borderColor: "teal",
        fill: false,
        showLine: false,
        pointRadius: 2,
      },
    ],
  };
  const windDirectionOptions = {
    scales: {
      yAxes: [{ ticks: { min: 0, max: 360, stepSize: 90 } }],
      xAxes: [{}],
    },
  };

  const latest = series.length
    ? series[series.length - 1]
    : {
        temperature: null,
        humidity: null,
        precipitation: null,
        wind_speed: null,
        max_wind_speed: null,
        wind_direction: null,
      };

  return (
    <Navbar>
      <SimpleGrid minChildWidth="350px" spacing="4" mb={4}>
        <Box p={5} bg={bg} boxShadow="md" borderRadius="md">
          <Stack direction={{ base: "column", sm: "row" }} mb={4} px={4} align="center">
            <Heading as="h2" size="lg" mb={{ base: 4, sm: 0 }}>
              {device?.device_name || (
                <>
                  <Trans>Device</Trans>
                  {" #"}
                  {device?.device_id}
                </>
              )}
            </Heading>
            <Spacer />
            <Button
              as={Link}
              to={`/devices/${id}/edit`}
              colorScheme="teal"
              variant="outline"
              size="sm"
              mb={{ base: 4, sm: 0 }}
            >
              Edit
            </Button>
          </Stack>
          <Heading size="sm" mb={2}>
            Current Data:
          </Heading>
          {deviceLoading ? <Skeleton height="80px" /> : <DataDump device={device} />}
        </Box>
        <Show above="lg">
          <Box p={5} bg={bg} boxShadow="md" borderRadius="md" height="300px">
            <DeviceMap device={device} />
          </Box>
        </Show>
      </SimpleGrid>
      <Show below="lg">
        <Box p={5} bg={bg} boxShadow="md" borderRadius="md" height="300px" mb={4}>
          <DeviceMap device={device} />
        </Box>
      </Show>
      <Box maxW="95%" mx="auto" p={4} bg={bg} borderRadius="md">
        <Heading mb={4}>Weather Station Data</Heading>
        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4} mb={4}>
          <Select value={lastDays} onChange={(e) => setLastDays(+e.target.value)}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
          <Select value={interval} onChange={(e) => setInterval(e.target.value)}>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
          </Select>
        </SimpleGrid>
        {loading ? (
          <Skeleton height="400px" />
        ) : (
          <>
            <Box mb={4}>
              <Heading size="md" mb={2}>
                Current Conditions
              </Heading>
              <SimpleGrid columns={6} spacing={4}>
                <Box>
                  Temperature:{" "}
                  {latest.temperature != null ? latest.temperature.toFixed(1) + "°C" : "N/A"}
                </Box>
                <Box>
                  Humidity: {latest.humidity != null ? latest.humidity.toFixed(1) + "%" : "N/A"}
                </Box>
                <Box>
                  Precipitation:{" "}
                  {latest.precipitation != null ? latest.precipitation.toFixed(2) + " mm" : "N/A"}
                </Box>
                <Box>
                  Wind Speed:{" "}
                  {latest.wind_speed != null ? latest.wind_speed.toFixed(1) + " m/s" : "N/A"}
                </Box>
                <Box>
                  Wind Gust:{" "}
                  {latest.max_wind_speed != null
                    ? latest.max_wind_speed.toFixed(1) + " m/s"
                    : "N/A"}
                </Box>
                <Box>
                  Wind Direction:{" "}
                  {latest.wind_direction != null ? latest.wind_direction.toFixed(0) + "°" : "N/A"}
                </Box>
              </SimpleGrid>
            </Box>
            {/* Charts */}
            <SimpleGrid columns={{ base: 1, lg: 2, "2xl": 3 }} spacing={4}>
              <Box>
                <Heading size="md" mb={2}>
                  Temperature
                </Heading>
                <Box>
                  <Line height={250} data={tempData} options={chartOptions} />
                </Box>
              </Box>
              <Box>
                <Heading size="md" mb={2}>
                  Humidity
                </Heading>
                <Box>
                  <Line height={250} data={humidityData} options={chartOptions} />
                </Box>
              </Box>
              <Box>
                <Heading size="md" mb={2}>
                  Precipitation
                </Heading>
                <Box>
                  <Bar height={250} data={precipData} options={chartOptions} />
                </Box>
              </Box>
              <Box>
                <Heading size="md" mb={2}>
                  Wind Speed
                </Heading>
                <Box>
                  <Line height={250} data={windSpeedData} options={chartOptions} />
                </Box>
              </Box>
              <Box>
                <Heading size="md" mb={2}>
                  Wind Gust
                </Heading>
                <Box>
                  <Line height={250} data={windGustData} options={chartOptions} />
                </Box>
              </Box>
              <Box>
                <Heading size="md" mb={2}>
                  Wind Direction
                </Heading>
                <Box>
                  <Line height={250} data={windDirectionData} options={windDirectionOptions} />
                </Box>
              </Box>
            </SimpleGrid>
          </>
        )}
      </Box>
    </Navbar>
  );
};

export default WeatherStationDetailView;
